import React, { ReactElement, useContext } from 'react';
import { Immutable } from '@wds/utils';
import { Sheet } from '@wds/sheet';
import { Typography } from '@wds/typography';
import { List } from '@wds/list';
import { Box } from '@wds/box';
import { IconHelpFilled } from '@wds/icons';
import { t, defineMessages } from '@sm/intl';
import { Responsive } from '@wds/responsive';
import { MenuItems } from './MenuItems';
import HeaderContext from '../../HeaderContext';
import { NavButton } from '../nav-button';
import useStyles from './useStyles';
import { getHelpCenterLink } from '../../../../../helpers/getHelpLink';
import { getResourcesItems } from '../../../components/Drawer/getDrawerData';
import { StaticContext } from '../../../../StaticContext';

const COPYHELP = defineMessages({
  HELP: {
    id: 'HelpMenu.help',
    defaultMessage: 'Help',
    description:
      '[Type: Label][Vis.: high] - This is the Help title of the side menu.',
  },
});

export type HelpMenuProps = Immutable<{
  showHelp: boolean;
}> & {
  toggleHelp(): void;
};

const HelpMenu = ({ showHelp, toggleHelp }: HelpMenuProps): ReactElement => {
  const classes = useStyles();
  const { lang } = useContext(HeaderContext);
  const itemsData = getResourcesItems(lang) || [];

  const { HEADER_EXP = {} } = useContext(StaticContext);

  const { hostname } = window.location;
  let helpUrl = getHelpCenterLink(lang, { path: 'embed' });

  if (hostname.includes('localmonkey') || hostname.includes('monkeytest')) {
    helpUrl = helpUrl.replace('surveymonkey', 'monkeytest3');
  }

  // Help center iframe experiment
  // https://momentiveai.atlassian.net/browse/ROADMAP-4595
  // https://admin.monkeytest3.com/experiments/surveymonkey/698746
  const helpPanelTreatment = HEADER_EXP?.helpPanel?.treatment;
  const showHelpCenterIframe =
    helpPanelTreatment === 'header_help_center_iframe_variant';

  return (
    <Responsive minWidth="xl">
      {matches =>
        matches ? (
          <>
            <NavButton
              onClick={toggleHelp}
              label="Open Help menu"
              active={showHelp}
            >
              <IconHelpFilled title="help icon" size="lg" />
            </NavButton>
            <Sheet show={showHelp} onClose={toggleHelp} offsetTop={48}>
              {showHelpCenterIframe ? (
                <iframe
                  title="Help center"
                  width="100%"
                  height="100%"
                  src={helpUrl}
                  className={classes.helpIframe}
                />
              ) : (
                <>
                  <Box pt={5} pl={5} pr={5}>
                    <Typography variant="sectionTitle">
                      {t(COPYHELP.HELP)}
                    </Typography>
                  </Box>
                  <List interactive noRules>
                    {itemsData.map(
                      props =>
                        props && <MenuItems {...props} key={props.title} />
                    )}
                  </List>
                </>
              )}
            </Sheet>
          </>
        ) : (
          itemsData.map(
            props => props && <MenuItems {...props} key={props.title} />
          )
        )
      }
    </Responsive>
  );
};

export default HelpMenu;
export { HelpMenu };
